import type { TFunction } from 'i18next';
import type { BriefTemplateContent } from '@fluenceur/types';
import { isGiftedProductValued } from '@/pages/collabs/brief-templates/_shared/brief-content';

/**
 * Blank brief the wizard's "write from scratch" path starts from. Mirrors the
 * backend's empty-content defaults: empty narrative, the design's legal notice,
 * platform label required, no usage-rights channels, no deliverables, null
 * timeline, and an unconfirmed 'none' compensation (the terms step forces an
 * explicit pick).
 *
 * KEEP IN SYNC: if you change these defaults, check the backend mirror in
 * apps/backend/app/services/collabs/brief_content_normalizer.ts
 * (emptyBriefContent / normalizeBriefContent) — the two must produce the same
 * shape or a blank wizard brief and a backend-defaulted one will diverge.
 */
export const emptyCollabContent = (transparencyNotice: string): BriefTemplateContent => ({
  concept: { body: '' },
  guidelines: {
    dos: '',
    donts: '',
    caption: {
      hashtags: [],
      mentions: { instagram: null, tiktok: null },
      transparencyNotice,
      requirePlatformLabel: true,
    },
  },
  usageRights: { durationMonths: null, channels: [] },
  deliverables: [],
  timeline: { firstContentDays: null, endDays: null },
  compensation: { type: 'none', fixedPayment: null, giftedProduct: null },
  attachments: [],
  // A draft states no clause and names no party: both are written by the send,
  // which is the act that turns a draft into an offer. Written explicitly
  // rather than left absent so this literal and the backend's empty content
  // are the same shape, which is the whole point of the mirror above.
  clauses: [],
  parties: null,
});

/**
 * Whether a brief carries a deliberate compensation on its own: a fixed payment
 * with a positive amount, or a gifted product actually selected. A bare 'none'
 * is NOT deliberate — the terms step must confirm it explicitly.
 */
export const hasDeliberateCompensation = (content: BriefTemplateContent): boolean => {
  const { compensation } = content;
  if (compensation.type === 'fixed_payment') {
    return (compensation.fixedPayment?.amountCents ?? 0) > 0;
  }
  if (compensation.type === 'gifted_product') {
    const gifted = compensation.giftedProduct;
    return Boolean(
      gifted && (gifted.catalogProducts?.length || gifted.catalog || gifted.manual?.name?.trim())
    );
  }
  return false;
};

/**
 * Compensation validity for sending: an explicit type where fixed payment needs
 * an amount and gifted needs a product *and its value*; 'none' is allowed as a
 * deliberate pick.
 *
 * The value rule is the editor's, imported rather than restated: the wizard
 * used to stop at "a product was chosen", so an unpriced gift left the tunnel
 * and came back as a 422 — which in a bulk send lands on the results screen,
 * with the terms step that owns the control several screens behind.
 *
 * Deliberately not folded into `hasDeliberateCompensation`: that one answers
 * whether the brand made a pick, and the brief step reads it to decide whether
 * the choice still needs confirming.
 */
export const isCompensationSendable = (content: BriefTemplateContent): boolean => {
  const { compensation } = content;
  if (compensation.type === 'none') return true;
  if (!hasDeliberateCompensation(content)) return false;
  if (compensation.type === 'gifted_product') return isGiftedProductValued(compensation);
  return true;
};

/**
 * Public URL of a recipient's personal link. Base is configurable per
 * environment (turbo already forwards VITE_*); falls back to the production
 * recipient surface.
 */
export const collabLinkUrl = (lookupKey: string): string => {
  const base = import.meta.env.VITE_COLLAB_LINK_BASE_URL ?? 'https://collab.fluenceur.com/c';
  return `${base.replace(/\/$/, '')}/${lookupKey}`;
};

/**
 * How the wizard names a recipient in its sentences. A prospect the brand's
 * plan masks comes with an empty name until the collab is accepted: it reads
 * « Créateur masqué » rather than leaving a sentence without its name.
 */
export const recipientNameFor = (
  recipient: { displayName: string } | null | undefined,
  t: TFunction
): string => recipient?.displayName.trim() || t('planLocks.name');
